import QRCode from 'qrcode';
import analytics from './analytics';

/**
 * QR Code utilities for referral tracking
 */

export const generateReferralQR = async (referralCode, options = {}) => {
  const baseUrl = options.baseUrl || 'https://thecrappermapper.com';
  const url = `${baseUrl}/qrcode?ref=${referralCode}`;
  
  try {
    const qrDataUrl = await QRCode.toDataURL(url, {
      width: options.width || 200,
      margin: options.margin || 2,
      color: {
        dark: options.darkColor || '#000000',
        light: options.lightColor || '#FFFFFF'
      },
      errorCorrectionLevel: options.errorCorrectionLevel || 'M'
    });
    
    return {
      qrDataUrl,
      url,
      referralCode
    };
  } catch (error) {
    console.error('Error generating QR code:', error);
    return null;
  }
};

/**
 * Generate multiple QR codes for different referral codes
 */
export const generateMultipleReferralQRs = async (referralCodes, options = {}) => {
  const results = [];
  
  for (const code of referralCodes) {
    const qrCode = await generateReferralQR(code, options);
    if (qrCode) {
      results.push(qrCode);
    }
  }
  
  return results;
};

/**
 * Handle referral tracking on page load
 */
export const handleReferralTracking = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const referralCode = urlParams.get('ref');

  if (referralCode) {
    // Store referral code for later use (registration conversion)
    localStorage.setItem('referral_code', referralCode);
    
    // Track the referral visit
    analytics.trackReferralVisit(referralCode, window.location.pathname);
    
    // Track QR code scan specifically if coming from QR landing
    if (window.location.pathname.includes('qrcode')) {
      analytics.trackQRCodeScan(referralCode);
    }
    
    return referralCode;
  }
  
  return null;
};

/**
 * Get stored referral code for registration tracking
 */
export const getStoredReferralCode = () => {
  return localStorage.getItem('referral_code');
};

/**
 * Clear stored referral code after conversion
 */
export const clearStoredReferralCode = () => {
  localStorage.removeItem('referral_code');
};

/**
 * Track referral conversion when user registers
 */
export const trackReferralConversion = (user, referralCode = null) => {
  const code = referralCode || getStoredReferralCode();
  
  if (code) {
    analytics.track('referral_conversion', {
      user_id: user.id,
      username: user.username,
      referral_code: code,
      conversion_time: new Date().toISOString()
    });
    
    // Clear referral code after successful conversion
    clearStoredReferralCode();
  }
};

/**
 * Generate referral codes for users
 */
export const generateUserReferralCode = (userId) => {
  // Simple format: user ID + random suffix for uniqueness
  const randomSuffix = Math.random().toString(36).substring(2, 8);
  return `${userId}-${randomSuffix}`;
};

/**
 * Create QR landing component data
 */
export const createQRLandingData = (referralCode) => {
  return {
    referralCode,
    trackingUrl: `https://thecrappermapper.com/qrcode?ref=${referralCode}`,
    appUrl: 'https://thecrappermapper.com',
    qrCodeOptions: {
      width: 300,
      margin: 3,
      darkColor: '#5752e8', // Brand indigo token (see brandTokens.js)
      lightColor: '#FFFFFF'
    }
  };
};
